/**
 * batch-61 klant-onboarding-docgen (FO-450) - de relatie-onboarding IN het
 * klantportaal: gekoppeld aan de rondleiding, voorgevuld uit wat STREEP al van
 * de relatie weet, hervatbaar, en na intern akkoord als document in het portaal.
 *
 * Gedeeld door de API (nav-info + `/client-portal/onboarding`), de klant-app
 * (tour-eindstap, dashboard-banner, wizard-prefill) en de tests. Bewust
 * DB-onafhankelijk (string-unions, geen Prisma-import), net als
 * `client-onboarding.ts`.
 */
import type { ClientOnboardingCategorie, ClientOnboardingStatus } from './client-onboarding'
import type { KlantControleBlok } from './klantenportaal-verbeterronde'

// ─── Tour-eindstap (server-side beslist) ─────────────────────────────────────

/**
 * Waar de rondleiding in eindigt. `onboarding` = er ligt een open (nog niet
 * ingediende) relatie-onboarding voor deze klant: de tour loopt over in de
 * wizard. `controle` = de bestaande gegevens-controlestap (D2): geen
 * onboarding, al ingediend/voltooid, of intern geannuleerd.
 */
export type KlantTourEindstap = 'controle' | 'onboarding'

/**
 * Compacte onboarding-stand voor de klant-shell (nav-info): stuurt de
 * tour-eindstap en de dashboard-banner "Onboarding {n}/{m}". Onboarding is PER
 * KLANT; de rondleiding per gebruiker (gebruiker B na een voltooide onboarding
 * krijgt alleen de controlestap, zie `bepaalTourEindstap`).
 */
export interface KlantOnboardingStandDTO {
  onboardingId: string
  status: ClientOnboardingStatus
  /** Aantal klant-secties dat de klant heeft afgerond. */
  afgerond: number
  /** Aantal klant-secties in totaal. */
  totaal: number
  /** Secties die intern zijn afgekeurd en op correctie wachten. */
  afgekeurd: number
  /** True zolang de klant nog kan invullen (CONCEPT/WACHT_OP_KLANT/IN_BEHANDELING). */
  kanInvullen: boolean
  /** True als de klant heeft ingediend en STREEP beoordeelt (WACHT_OP_REVIEW/INTERN_AANVULLEN). */
  wachtOpReview: boolean
  deadline: string | null
}

/** Statussen waarin de KLANT nog aan de vragenlijst kan werken. */
export const KLANT_ONBOARDING_INVUL_STATUSSEN: readonly ClientOnboardingStatus[] = [
  'CONCEPT',
  'WACHT_OP_KLANT',
  'IN_BEHANDELING',
]

/** Statussen waarin de onboarding intern in beoordeling is (klant wacht). */
export const KLANT_ONBOARDING_REVIEW_STATUSSEN: readonly ClientOnboardingStatus[] = [
  'WACHT_OP_REVIEW',
  'INTERN_AANVULLEN',
]

/**
 * De tour-eindstap uit de onboarding-stand (puur). Alleen een onboarding waar
 * de klant nog IETS mee kan (invullen of corrigeren) verdient de wizard als
 * eindstap; al het andere valt terug op de controlestap - inclusief een
 * onboarding die op beoordeling wacht (daar kan de klant niets aan doen) en de
 * situatie "collega A heeft 'm al ingediend/voltooid".
 */
export function bepaalTourEindstap(stand: KlantOnboardingStandDTO | null): KlantTourEindstap {
  if (!stand) return 'controle'
  return stand.kanInvullen ? 'onboarding' : 'controle'
}

/** Onboarding-blok in de nav-info-`tour` (null = geen lopende onboarding). */
export interface KlantTourOnboardingDTO {
  eindstap: KlantTourEindstap
  stand: KlantOnboardingStandDTO | null
}

// ─── Wizard-prefill in het portaal ───────────────────────────────────────────

/** Waar een voorgevulde sectie vandaan komt (voor de "klopt dit?"-banner). */
export type KlantOnboardingPrefillBron = 'RELATIE' | 'EERDERE_BEVESTIGING'

export interface KlantOnboardingPrefillSectie {
  /** formulierData-vorm van de sectie (zie `OnboardingFormulierDataMap`). */
  data: unknown
  bron: KlantOnboardingPrefillBron
  /** Laatste D2-bevestiging van het bijbehorende controleblok, indien bekend. */
  bevestigdOp: string | null
}

/**
 * Prefill per KLANT-sectie voor de portaal-wizard: alleen voor secties die de
 * klant nog niet heeft ingevuld (`formulierData` null) - een al ingevulde
 * sectie is de waarheid van de klant en wordt nooit overschreven.
 */
export type KlantOnboardingPrefill = Partial<
  Record<ClientOnboardingCategorie, KlantOnboardingPrefillSectie>
>

/**
 * Koppeling sectie -> D2-controleblok. Bevestigt de klant zo'n sectie
 * ongewijzigd (prefill == ingevuld), dan telt dat als D2-bevestiging van het
 * blok; wijzigt hij iets, dan loopt het via de onboarding-review (het
 * bestaande wijziging-voorstel-mechanisme van de onboarding), nooit direct.
 */
export const ONBOARDING_SECTIE_CONTROLE_BLOK: Partial<
  Record<ClientOnboardingCategorie, KlantControleBlok>
> = {
  FACTURATIE: 'FACTURATIE',
  WERKSCHEMA: 'ROOSTER',
  SLUITINGSDAGEN: 'SLUITINGSDAGEN',
}

/** Portaal-payload: de publieke onboarding-vorm plus prefill + schrijfrecht. */
export interface KlantOnboardingPortaalMeta {
  /** OWNER/ADMIN zonder impersonatie mogen invullen; anderen zien alleen de stand. */
  kanSchrijven: boolean
  prefill: KlantOnboardingPrefill
  /** Laatste D2-bevestiging per blok (voor de "eerder bevestigd"-hint). */
  controleBevestigingen: Array<{ blok: KlantControleBlok; bevestigdOp: string }>
}

// ─── Onboarding-document ─────────────────────────────────────────────────────

/** Slug van het (geseede) sjabloon waarmee het onboarding-document rendert. */
export const KLANT_ONBOARDING_DOCUMENT_TEMPLATE_SLUG = 'klant-onboarding-bevestiging'

/** Placeholder-keys van dat sjabloon (één tekstblok per sectie, regeleinden toegestaan). */
export const KLANT_ONBOARDING_DOCUMENT_KEYS = [
  'klant_naam',
  'datum',
  'bevestigd_door',
  'bevestigd_op',
  'bedrijfsgegevens',
  'contactpersonen',
  'locaties',
  'facturatie',
  'werkschema',
  'sluitingsdagen',
  'monteurprofiel',
  'veiligheid',
] as const
export type KlantOnboardingDocumentKey = (typeof KLANT_ONBOARDING_DOCUMENT_KEYS)[number]

/** Bestandstitel: "Onboarding {klant} - {datum}" (datum dd-MM-yyyy, Europe/Amsterdam). */
export function onboardingDocumentTitel(klantNaam: string, datumNl: string): string {
  return `Onboarding ${klantNaam} - ${datumNl}`
}
